/**
 * BatchResultsPage
 *
 * Route: /batch/:jobId  — shows ranked batch results table + summary bar
 * Route: /batch         — shows a simple Job ID entry form (no jobId yet)
 *
 * Sort is applied client-side (results arrive pre-sorted by composite_score
 * but user can re-sort by any column). Null values are pushed to the bottom.
 *
 * Filter state is synced to URL search params so browser back restores the
 * view — pattern matches useLeadFilters.ts (setSearchParams replace:true).
 *
 * Plan 02 additions:
 *  - BatchFilterToolbar (verdict dropdown, min score, market, presets)
 *  - URL-synced filter state via useSearchParams
 *  - onPropertyClick via useNavigate → /property/:id
 *  - Checkbox selection + comparison modal
 *  - CSV export (filtered visible rows)
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useBatchJob } from '../hooks/useBatchJob';
import { usePropertyComparison } from '../hooks/usePropertyComparison';
import { BatchSummaryBar } from '../components/Batch/BatchSummaryBar';
import { BatchResultsTable } from '../components/Batch/BatchResultsTable';
import { BatchFilterToolbar, DEFAULT_BATCH_FILTERS } from '../components/Batch/BatchFilterToolbar';
import { BatchComparisonModal } from '../components/Batch/BatchComparisonModal';
import { BatchResultsExport } from '../components/Batch/BatchResultsExport';
import type { BatchPropertyResult } from '../types/batch';
import type { BatchFilters } from '../components/Batch/BatchFilterToolbar';

// ---------------------------------------------------------------------------
// IRR threshold for High IRR preset (15%)
// ---------------------------------------------------------------------------

const HIGH_IRR_THRESHOLD = 0.15;

// ---------------------------------------------------------------------------
// URL param serialisation/deserialisation
// ---------------------------------------------------------------------------

function filtersToParams(filters: BatchFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.verdict !== 'all') p.set('verdict', filters.verdict);
  if (filters.minComposite !== null) p.set('minScore', String(filters.minComposite));
  if (filters.topN !== null) p.set('topN', String(filters.topN));
  if (filters.market !== null) p.set('market', filters.market);
  if (filters.preset !== 'none') p.set('preset', filters.preset);
  return p;
}

function filtersFromParams(params: URLSearchParams): BatchFilters {
  const f: BatchFilters = { ...DEFAULT_BATCH_FILTERS };

  const verdict = params.get('verdict');
  if (verdict === 'GO' || verdict === 'NO-GO' || verdict === 'CONDITIONAL') {
    f.verdict = verdict;
  }

  const minScore = params.get('minScore');
  if (minScore) {
    const n = parseInt(minScore, 10);
    if (!isNaN(n)) f.minComposite = n;
  }

  const topN = params.get('topN');
  if (topN) {
    const n = parseInt(topN, 10);
    if (!isNaN(n)) f.topN = n;
  }

  const market = params.get('market');
  if (market) f.market = market;

  const preset = params.get('preset');
  if (preset === 'go-only' || preset === 'top-10' || preset === 'high-irr') {
    f.preset = preset;
  }

  return f;
}

// ---------------------------------------------------------------------------
// Sort helper
// ---------------------------------------------------------------------------

/**
 * Compare two BatchPropertyResult rows for a given sort column.
 * Returns negative if a < b (a should come before b for ascending).
 * Null values are always pushed to the bottom regardless of direction.
 */
function compareRows(
  a: BatchPropertyResult,
  b: BatchPropertyResult,
  col: string,
  desc: boolean
): number {
  // Extract raw values (cast through unknown to satisfy strict index-signature check)
  const aVal = (a as unknown as Record<string, unknown>)[col] ?? null;
  const bVal = (b as unknown as Record<string, unknown>)[col] ?? null;

  // Nulls always last
  if (aVal === null && bVal === null) return 0;
  if (aVal === null) return 1;
  if (bVal === null) return -1;

  // String comparison
  if (typeof aVal === 'string' && typeof bVal === 'string') {
    const cmp = aVal.localeCompare(bVal);
    return desc ? -cmp : cmp;
  }

  // Numeric comparison
  const aN = Number(aVal);
  const bN = Number(bVal);
  if (isNaN(aN) || isNaN(bN)) return 0;
  return desc ? bN - aN : aN - bN;
}

// ---------------------------------------------------------------------------
// Apply filters to sorted results
// ---------------------------------------------------------------------------

function applyFilters(
  sortedResults: BatchPropertyResult[],
  filters: BatchFilters
): BatchPropertyResult[] {
  let filtered = sortedResults;

  // Verdict filter
  if (filters.verdict !== 'all') {
    filtered = filtered.filter((r) => r.deal_verdict === filters.verdict);
  }

  // Min composite score
  if (filters.minComposite !== null) {
    filtered = filtered.filter(
      (r) => r.composite_score !== null && r.composite_score !== undefined && r.composite_score >= filters.minComposite!
    );
  }

  // High IRR preset — irr_5yr >= 15%
  if (filters.preset === 'high-irr') {
    filtered = filtered.filter(
      (r) => r.irr_5yr !== null && r.irr_5yr !== undefined && r.irr_5yr >= HIGH_IRR_THRESHOLD
    );
  }

  // Market filter
  if (filters.market !== null) {
    // BatchPropertyResult doesn't have a market_id column directly exposed
    // — match against neighborhood as market proxy (market_id is in BatchJobState)
    filtered = filtered.filter((r) => r.neighborhood === filters.market);
  }

  // Top N (slice — applied after all other filters)
  if (filters.topN !== null) {
    filtered = filtered.slice(0, filters.topN);
  }

  return filtered;
}

// ---------------------------------------------------------------------------
// Landing page (no jobId)
// ---------------------------------------------------------------------------

function BatchLanding() {
  const [inputId, setInputId] = useState('');
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputId.trim();
    if (trimmed) navigate(`/batch/${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-text-primary mb-2">Batch Results</h1>
        <p className="text-text-secondary text-sm">Enter a batch job ID to view ranked underwriting results.</p>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={inputId}
          onChange={(e) => setInputId(e.target.value)}
          placeholder="Job ID (e.g. batch_abc123)"
          className="px-3 py-2 rounded-lg border border-border bg-bg-card text-text-primary text-sm w-72 focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <button
          type="submit"
          disabled={!inputId.trim()}
          className="px-4 py-2 rounded-lg bg-accent text-bg-primary text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
        >
          View Results
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Results view (jobId present)
// ---------------------------------------------------------------------------

function BatchResults({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sort state
  const [sortBy, setSortBy] = useState('composite_score');
  const [sortDescending, setSortDescending] = useState(true);

  // Filter state — derived from URL params
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  // Comparison selection
  const {
    selectedIds: selectedIdsStr,
    toggleSelection,
    comparisonReady,
    selectionCount,
    removeFromComparison,
    clearComparison,
  } = usePropertyComparison();

  // Convert string-keyed Set → number Set for table prop
  const selectedIdsNum = useMemo<Set<number>>(
    () => new Set([...selectedIdsStr].map(Number)),
    [selectedIdsStr]
  );

  const [showComparison, setShowComparison] = useState(false);

  const { data, isLoading, error, results } = useBatchJob(jobId);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleSort = (col: string) => {
    if (col === sortBy) {
      setSortDescending((prev) => !prev);
    } else {
      setSortBy(col);
      setSortDescending(true);
    }
  };

  const handleFilterChange = (updates: Partial<BatchFilters>) => {
    const next = { ...filters, ...updates };
    setSearchParams(filtersToParams(next), { replace: true });
  };

  const handlePropertyClick = (id: number) => {
    navigate(`/property/${id}`);
  };

  const handleToggleSelect = (id: number) => {
    toggleSelection(String(id));
  };

  // -------------------------------------------------------------------------
  // Derived: sorted then filtered results
  // -------------------------------------------------------------------------

  const sortedResults = useMemo(
    () => [...results].sort((a, b) => compareRows(a, b, sortBy, sortDescending)),
    [results, sortBy, sortDescending]
  );

  const filteredResults = useMemo(
    () => applyFilters(sortedResults, filters),
    [sortedResults, filters]
  );

  // Available markets for dropdown
  const availableMarkets = useMemo(() => {
    const markets = new Set<string>();
    results.forEach((r) => {
      if (r.neighborhood) markets.add(r.neighborhood);
    });
    return [...markets].sort();
  }, [results]);

  // Properties selected for comparison (from filtered results)
  const selectedProperties = useMemo(
    () => filteredResults.filter((r) => selectedIdsNum.has(r.property_id)),
    [filteredResults, selectedIdsNum]
  );

  // -------------------------------------------------------------------------
  // Loading / error states
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-3 text-text-secondary">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <span className="text-sm">Loading batch results…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-2">
        <p className="text-red-400 font-semibold">Failed to load batch job</p>
        <p className="text-text-secondary text-sm">{error.message}</p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-1">
      {/* Status bar */}
      <div className="flex items-center justify-between text-xs text-text-secondary mb-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-text-primary">{jobId}</span>
          {data?.status && (
            <span className="px-2 py-0.5 rounded bg-bg-card border border-border capitalize">
              {data.status.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <span>
          Stage 1: {data?.stage1_complete ?? 0}/{data?.total_properties ?? 0} &nbsp;·&nbsp;
          Stage 2: {data?.stage2_complete ?? 0}/{data?.finalist_ids.length ?? 0}
        </span>
      </div>

      {/* Summary stats bar */}
      <BatchSummaryBar results={filteredResults} />

      {/* Filter toolbar */}
      <BatchFilterToolbar
        filters={filters}
        onFilterChange={handleFilterChange}
        resultCount={filteredResults.length}
        totalCount={results.length}
        availableMarkets={availableMarkets}
      />

      {/* Action bar: Compare + Export */}
      <div className="flex items-center gap-2 pt-1">
        {/* Compare button */}
        <button
          disabled={!comparisonReady}
          onClick={() => setShowComparison(true)}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-bg-card border-border hover:border-accent/50 hover:text-accent text-text-secondary"
        >
          Compare ({selectionCount})
        </button>

        {/* Clear selection */}
        {selectionCount > 0 && (
          <button
            onClick={clearComparison}
            className="px-2 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors underline underline-offset-2"
          >
            Clear selection
          </button>
        )}

        {/* Spacer */}
        <span className="flex-1" />

        {/* Export CSV */}
        <BatchResultsExport results={filteredResults} />
      </div>

      {/* Results table */}
      <BatchResultsTable
        results={filteredResults}
        sortBy={sortBy}
        sortDescending={sortDescending}
        onSort={handleSort}
        onPropertyClick={handlePropertyClick}
        selectedIds={selectedIdsNum}
        onToggleSelect={handleToggleSelect}
      />

      {/* Comparison modal */}
      {showComparison && (
        <BatchComparisonModal
          open={showComparison}
          onClose={() => setShowComparison(false)}
          properties={selectedProperties}
          onRemove={(id) => removeFromComparison(String(id))}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function BatchResultsPage() {
  const { jobId } = useParams<{ jobId?: string }>();

  return (
    <div className="px-4 py-6 max-w-screen-2xl mx-auto">
      {/* Breadcrumbs */}
      <nav className="text-xs text-text-secondary mb-4 flex items-center gap-1.5">
        <a href="/" className="hover:text-text-primary transition-colors">Home</a>
        <span>/</span>
        <span className="text-text-primary">Batch Results</span>
        {jobId && (
          <>
            <span>/</span>
            <span className="text-accent font-mono">{jobId}</span>
          </>
        )}
      </nav>

      {jobId ? <BatchResults jobId={jobId} /> : <BatchLanding />}
    </div>
  );
}

export default BatchResultsPage;
